import { formatCents, formatCount, formatPercent, tt, formatDecimal } from '@agos/shared'

/**
 * The campaigns tab of `/loyalty-program` — fifteen campaign types, two mini-games, the wheel, the
 * leaderboard and the referral rules.
 *
 * **FB-166.** The catalogue holds **fifteen** types and legacy renders **thirteen**. `CAMPAIGN_GROUPS` covers
 * twelve across five groups and `rebricek` is rendered on its own; **`push_blast` is on no screen** — while
 * legacy puts it in `MANUAL_RUNNABLE` *and* `NOTIFICATION_CAMPAIGN_TYPES`, declaring it runnable and
 * notification-bearing with no card to run it from.
 *
 * `spin_wheel` is also absent from the cards, and that is **right**. `CampaignCatalog`'s docblock:
 *
 * > `spin_wheel` is in the enum and has no defaults: the wheel keeps its configuration in
 * > `wheel_campaign_settings` rather than in the campaign row, so the row exists only to give the wheel a
 * > place in the list and a sent counter.
 *
 * Nothing in the backend reads that row's `is_active`. One unreachable campaign, not two — and the port says
 * which is which, because a screen that hides a working campaign and a screen that hides an inert row are
 * different mistakes.
 *
 * **FB-167.** Every campaign reports `redeemedCount: 0`, and it is structural. 1 657 coupons sent across the
 * three active campaigns and not one redemption — because no `UPDATE` of `redeemed_count` exists in either
 * codebase. This is FB-113 seen at its source: `/analytics` reports the rate, and this is the screen that
 * feeds it. Legacy prints the zero beside the sent count as though it were a measurement.
 *
 * **FB-168.** A refusal is recorded as a run. `cashback`'s newest `campaign_runs` row is stamped 29 July with
 * `recipientCount: 0` and the error *"Tento typ kampane sa nedá spustiť manuálne (spúšťa sa automaticky po
 * objednávke)"*. Legacy's card shows *last run* from the same row, so a campaign that refused to run reads as
 * one that ran and reached nobody — which is also what `meninova_zlava` genuinely did on 28 July.
 */

// ─── The catalogue ───────────────────────────────────────────────────────────

/** Every value of the `campaign_type` enum, in the order Postgres declares it. */
export const CAMPAIGN_TYPES: readonly string[] = [
  'meninova_zlava',
  'kuponove_tornado',
  'kupon_za_vernost',
  'reaktivacia_neaktivnych',
  'cashback',
  'happy_hours',
  'personalizovana_ponuka',
  'rebricek',
  'click_and_win',
  'scratch_card',
  'spin_wheel',
  'push_blast',
  'black_friday',
  'welcome_first_order',
  'free_delivery_weekend',
]

/** The two the wheel and the games own outright — their settings live elsewhere. */
export const MINI_GAME_TYPES: readonly string[] = ['click_and_win', 'scratch_card']

/**
 * The campaign row `spin_wheel` keeps only so the wheel has a place in the list.
 *
 * Its `isActive` is inert by design and nothing reads it, so the card list leaves it out — as legacy does —
 * and the wheel panel carries the real switch.
 */
export const WHEEL_PLACEHOLDER_TYPE = 'spin_wheel'

export interface CampaignGroup {
  key: string
  types: readonly string[]
}

export const CAMPAIGN_GROUPS: readonly CampaignGroup[] = [
  { key: 'rewards', types: ['kupon_za_vernost', 'cashback', 'meninova_zlava', 'welcome_first_order'] },
  { key: 'reactivation', types: ['reaktivacia_neaktivnych'] },
  { key: 'blast', types: ['kuponove_tornado', 'personalizovana_ponuka', 'push_blast'] },
  { key: 'promo', types: ['happy_hours', 'black_friday', 'free_delivery_weekend'] },
  { key: 'gamification', types: ['click_and_win', 'scratch_card'] },
  { key: 'ranking', types: ['rebricek'] },
]

export function groupTitle(key: string): string {
  if (key === 'rewards') return tt('Vernostné odmeny')
  if (key === 'reactivation') return tt('Reaktivácia')
  if (key === 'blast') return tt('Hromadné akcie')
  if (key === 'promo') return tt('Časové akcie')
  if (key === 'gamification') return tt('Hry pre zákazníkov')
  return tt('Rebríček')
}

export function groupDescription(key: string): string {
  if (key === 'rewards') return tt('Odmena po splnení podmienky - objednávka, meniny, prvý nákup.')
  if (key === 'reactivation') return tt('Oslovenie zákazníkov, ktorí dlhšie neobjednávali.')
  if (key === 'blast') return tt('Plánované rozposlanie - kupóny, ponuky, push.')
  if (key === 'promo') return tt('Akcie viazané na čas. Zľavu aplikuje košík, nikomu sa nič neposiela.')
  if (key === 'gamification') return tt('Hru spúšťa zákazník sám. Kampaň len rozhoduje, či je dostupná.')
  return tt('Mesačný rebríček zákazníkov podľa tržby.')
}

/**
 * Which types the card list actually draws.
 *
 * The wheel's placeholder row is excluded and everything else in the enum is grouped — so a type added to the
 * backend appears somewhere instead of disappearing, which is FB-166's whole point.
 */
export function renderedTypes(): string[] {
  return CAMPAIGN_GROUPS.flatMap((group) => group.types)
}

/** A type in the enum that no group claims. Empty by construction; asserted, so it stays empty. */
export function unreachableTypes(): string[] {
  const rendered = new Set([...renderedTypes(), WHEEL_PLACEHOLDER_TYPE])
  return CAMPAIGN_TYPES.filter((type) => !rendered.has(type))
}

/** The types legacy leaves out, kept so a test can show the difference rather than describe it. */
export const LEGACY_UNRENDERED: readonly string[] = ['push_blast', 'spin_wheel']

export function campaignName(type: string): string {
  if (type === 'meninova_zlava') return tt('Meninová zľava')
  if (type === 'kuponove_tornado') return tt('Kupónové tornádo')
  if (type === 'kupon_za_vernost') return tt('Kupón za vernosť')
  if (type === 'reaktivacia_neaktivnych') return tt('Reaktivácia neaktívnych')
  if (type === 'cashback') return tt('Cashback')
  if (type === 'happy_hours') return tt('Happy hours')
  if (type === 'personalizovana_ponuka') return tt('Personalizovaná ponuka')
  if (type === 'rebricek') return tt('Rebríček')
  if (type === 'click_and_win') return tt('Klikni a vyhraj')
  if (type === 'scratch_card') return tt('Stieracia karta')
  if (type === 'spin_wheel') return tt('Koleso šťastia')
  if (type === 'push_blast') return tt('Push rozposlanie')
  if (type === 'black_friday') return tt('Black Friday')
  if (type === 'welcome_first_order') return tt('Vitajte - prvá objednávka')
  if (type === 'free_delivery_weekend') return tt('Doprava zadarmo')
  // A type the backend adds and this table has not learned yet shows its key rather than nothing.
  return type
}

export function isNamedCampaign(type: string): boolean {
  return campaignName(type) !== type
}

/** How a campaign starts. Only these four can be started by hand. */
export const MANUAL_RUNNABLE: readonly string[] = [
  'meninova_zlava',
  'reaktivacia_neaktivnych',
  'rebricek',
  'push_blast',
]

export type TriggerKind = 'schedule' | 'order' | 'checkout' | 'customer' | 'manual'

/**
 * What makes a campaign fire.
 *
 * Worth stating per card, because legacy's *Spustiť teraz* button appears on cards that refuse it and the
 * refusal only arrives after the click — see FB-168.
 */
export function triggerOf(type: string): TriggerKind {
  if (type === 'kupon_za_vernost' || type === 'cashback' || type === 'welcome_first_order') return 'order'
  if (type === 'happy_hours' || type === 'black_friday' || type === 'free_delivery_weekend') return 'checkout'
  if (MINI_GAME_TYPES.includes(type) || type === WHEEL_PLACEHOLDER_TYPE) return 'customer'
  if (type === 'push_blast') return 'manual'
  return 'schedule'
}

export function triggerMessage(trigger: TriggerKind): string {
  if (trigger === 'order') return tt('Spustí sa po objednávke, ktorá splní podmienku. Ručne nie.')
  if (trigger === 'checkout') return tt('Zľavu aplikuje košík. Nikomu sa nič neposiela.')
  if (trigger === 'customer') return tt('Spustí ju zákazník sám. Kampaň len rozhoduje, či je dostupná.')
  if (trigger === 'manual') return tt('Beží len ručne - sama sa nespustí nikdy.')
  return tt('Beží automaticky každý deň o 15:00. Ručne sa dá spustiť aj teraz.')
}

export function mayRunByHand(type: string): boolean {
  return MANUAL_RUNNABLE.includes(type)
}

// ─── The rows ────────────────────────────────────────────────────────────────

export interface CampaignRun {
  ranAt: string
  recipientCount: number
  successCount: number
  failedCount: number
  errorMessage: string | null
  /**
   * `ran` | `refused`, or `null` on a row written before `campaign_runs.status` existed — FB-168.
   *
   * `null` is not a default to fall back into quietly: those rows genuinely do not carry the fact, and
   * `runOutcome` infers for them the way it always did. Rows written since state it.
   */
  status: string | null
}

export interface Campaign {
  id: number
  type: string
  /** §10A.2's own name for this row. Null on all fifteen legacy rows; set when a type has several. */
  name: string | null
  isActive: boolean
  settings: Record<string, unknown>
  segment: Record<string, unknown>
  startsAt: string | null
  endsAt: string | null
  sentCount: number
  redeemedCount: number
  lastRun: CampaignRun | null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0
}

export function campaignsOf(payload: unknown): Campaign[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const type = text(row.type)
    if (type === null) return []
    const run = row.lastRun as Record<string, unknown> | null | undefined
    return [
      {
        id: typeof row.id === 'number' ? row.id : 0,
        type,
        name: text(row.name),
        isActive: row.isActive === true,
        settings: (row.settings ?? {}) as Record<string, unknown>,
        segment: (row.segment ?? {}) as Record<string, unknown>,
        startsAt: text(row.startsAt),
        endsAt: text(row.endsAt),
        sentCount: count(row.sentCount),
        redeemedCount: count(row.redeemedCount),
        lastRun:
          run === null || run === undefined || text(run.ranAt) === null
            ? null
            : {
                ranAt: text(run.ranAt) ?? '',
                recipientCount: count(run.recipientCount),
                successCount: count(run.successCount),
                failedCount: count(run.failedCount),
                errorMessage: text(run.errorMessage),
                status: text(run.status),
              },
      },
    ]
  })
}

/**
 * The rows to draw, grouped, with the wheel's placeholder left out.
 *
 * Keyed by type into a **list**, not a single row. §10A.2 lets one type have several campaigns —
 * `MarketingController`'s own comment says a type used to name exactly one and no longer does — so a lookup
 * that kept one row per type would draw the last and drop the rest without saying so. On this data every type
 * has exactly one, which is precisely when that bug would go unnoticed.
 */
export function groupedCampaigns(
  campaigns: readonly Campaign[],
): { key: string; campaigns: Campaign[] }[] {
  const byType = new Map<string, Campaign[]>()
  for (const campaign of campaigns) {
    const existing = byType.get(campaign.type)
    if (existing === undefined) byType.set(campaign.type, [campaign])
    else existing.push(campaign)
  }
  return CAMPAIGN_GROUPS.map((group) => ({
    key: group.key,
    campaigns: group.types.flatMap((type) => byType.get(type) ?? []),
  })).filter((group) => group.campaigns.length > 0)
}

/** The types the server sent more than one campaign for — each needs its own name to be told apart. */
export function typesWithSeveral(campaigns: readonly Campaign[]): string[] {
  const counts = new Map<string, number>()
  for (const campaign of campaigns) {
    counts.set(campaign.type, (counts.get(campaign.type) ?? 0) + 1)
  }
  return [...counts.entries()].filter(([, howMany]) => howMany > 1).map(([type]) => type)
}

/**
 * What to call one row.
 *
 * The catalogue name unless the row carries its own — which is how §10A.2's second campaign of a type is told
 * from the first. All fifteen rows on this data have `name: null`, so the catalogue answers for all of them.
 */
export function campaignLabel(campaign: Campaign): string {
  return campaign.name ?? campaignName(campaign.type)
}

/** A campaign's own window, if §10A.2 gave it one. Null on every legacy row. */
export function campaignWindow(campaign: Campaign): { from: string | null; to: string | null } | null {
  if (campaign.startsAt === null && campaign.endsAt === null) return null
  return { from: campaign.startsAt, to: campaign.endsAt }
}

/**
 * The window as a pair of `datetime-local` values — §9.262, owner O9.
 *
 * The column is a `timestamptz` and the input wants `YYYY-MM-DDTHH:mm` with no zone, so the stamp is
 * cut rather than parsed: a `Date` round-trip would move the value by the browser's offset, and a
 * campaign that starts an hour late on the customer's screen is exactly the bug this edits away.
 */
export function windowFormOf(campaign: Campaign): CampaignWindowForm {
  return { startsAt: localStampOf(campaign.startsAt), endsAt: localStampOf(campaign.endsAt) }
}

export interface CampaignWindowForm {
  startsAt: string
  endsAt: string
}

/**
 * A stored instant as the wall-clock time on the manager's own screen.
 *
 * **Not a substring of the stamp, which is what this was first written as.** The column is a
 * `timestamptz` and the server answers it in UTC — `2026-11-27T00:00Z`. Cutting the characters out
 * would put *00:00* in the box for a campaign that starts at **01:00** in Šurany, and the same
 * screen's read-only line above it prints `formatDateTime(startsAt)`, which is local. One screen,
 * two clocks, no warning.
 */
function localStampOf(stored: string | null): string {
  if (stored === null) return ''
  const instant = Date.parse(stored.trim())
  if (!Number.isFinite(instant)) return ''
  const local = new Date(instant)
  return (
    `${local.getFullYear()}-${pad2(local.getMonth() + 1)}-${pad2(local.getDate())}` +
    `T${pad2(local.getHours())}:${pad2(local.getMinutes())}`
  )
}

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

/**
 * A `datetime-local` value as an instant, with the offset that value means.
 *
 * **The offset has to be sent.** A zoneless stamp reaches `CAST(… AS timestamptz)` and PostgreSQL
 * resolves it against the *session* zone, which is `UTC` in this container — measured. So a Black
 * Friday set to begin at midnight began at 01:00, and at 02:00 in summer. Computed from the value
 * itself rather than from today, so a window set in November keeps CET while one set in July keeps
 * CEST.
 */
export function offsetStampOf(local: string): string {
  const typed = new Date(local)
  if (Number.isNaN(typed.getTime())) return local
  const minutes = -typed.getTimezoneOffset()
  const sign = minutes < 0 ? '-' : '+'
  const size = Math.abs(minutes)
  return `${local}:00${sign}${pad2(Math.floor(size / 60))}:${pad2(size % 60)}`
}

/**
 * What is wrong with the window somebody typed, in Slovak — the same two rules the creation form has,
 * because a window that ends before it starts is a campaign that never runs whichever screen set it.
 */
export function windowProblems(form: CampaignWindowForm): string[] {
  const found: string[] = []
  if (form.startsAt !== '' && !isStamp(form.startsAt)) found.push(tt('Neplatný dátum: začiatok'))
  if (form.endsAt !== '' && !isStamp(form.endsAt)) found.push(tt('Neplatný dátum: koniec'))
  if (
    form.startsAt !== '' &&
    form.endsAt !== '' &&
    isStamp(form.startsAt) &&
    isStamp(form.endsAt) &&
    form.endsAt.trim() <= form.startsAt.trim()
  ) {
    found.push(tt('Koniec musí byť po začiatku - inak kampaň nikdy nebeží'))
  }
  return found
}

/**
 * The body `PATCH /campaigns/id/{id}` takes for a window.
 *
 * **A cleared bound is `null`, and the key must be present to clear it** — the server's own comment
 * says so: *"A window bound is cleared by sending null, so containsKey is the test rather than the
 * value."* Omitting the key leaves the old bound in place, so a campaign somebody just un-ended would
 * still end.
 */
export function windowPayload(form: CampaignWindowForm): Record<string, unknown> {
  return {
    startsAt: form.startsAt.trim() === '' ? null : offsetStampOf(form.startsAt.trim()),
    endsAt: form.endsAt.trim() === '' ? null : offsetStampOf(form.endsAt.trim()),
  }
}

/** Whether the window on screen differs from the one stored. */
export function windowChanged(campaign: Campaign, form: CampaignWindowForm): boolean {
  const stored = windowFormOf(campaign)
  return stored.startsAt !== form.startsAt.trim() || stored.endsAt !== form.endsAt.trim()
}

/**
 * What a window means for a campaign that is switched on, said in Slovak.
 *
 * A campaign can be active and outside its window, and the row would otherwise read *aktívna* while
 * the checkout ignores it — `CampaignService.active()` filters on `withinWindow` before anything else.
 *
 * `nowMs` rather than a string, and the bounds are parsed rather than compared as text: the column is
 * a `timestamptz` and the browser is in Bratislava, so `'2026-11-25T00:00:00Z' < '2026-11-25T01:00'`
 * is a comparison of two different clocks that happens to look like one.
 */
export function windowNote(campaign: Campaign, nowMs: number): string {
  if (!campaign.isActive) return ''
  const from = campaign.startsAt === null ? null : Date.parse(campaign.startsAt)
  const to = campaign.endsAt === null ? null : Date.parse(campaign.endsAt)
  if (from !== null && Number.isFinite(from) && nowMs < from) {
    return tt('Zapnutá, ale ešte nezačala - zákazník ju zatiaľ nevidí.')
  }
  if (to !== null && Number.isFinite(to) && nowMs > to) {
    return tt('Zapnutá, ale už skončila - zákazník ju nevidí.')
  }
  return ''
}

/** Whether the row narrows its audience. `{}` on every legacy row — meaning everyone who qualifies. */
export function hasSegment(campaign: Campaign): boolean {
  return Object.keys(campaign.segment).length > 0
}

/** A row the server sent that no group claims — so a new enum value is visible rather than lost. */
export function ungroupedCampaigns(campaigns: readonly Campaign[]): Campaign[] {
  const claimed = new Set([...renderedTypes(), WHEEL_PLACEHOLDER_TYPE])
  return campaigns.filter((campaign) => !claimed.has(campaign.type))
}

export function activeCount(campaigns: readonly Campaign[]): number {
  return campaigns.filter((campaign) => campaign.isActive).length
}

export function totalSent(campaigns: readonly Campaign[]): number {
  return campaigns.reduce((sum, campaign) => sum + campaign.sentCount, 0)
}

export function totalRedeemed(campaigns: readonly Campaign[]): number {
  return campaigns.reduce((sum, campaign) => sum + campaign.redeemedCount, 0)
}

// ─── FB-167: the redemption rate that cannot exist ───────────────────────────

/**
 * The redemption rate, or `null` because it is not measured.
 *
 * `redeemed_count` had no `UPDATE` in either codebase, so the figure was structurally zero. A rate of
 * 0 % would be a measurement; *nemerané* is the truth. Same rule as FB-113 on `/analytics`, which reads
 * this column through a different endpoint.
 *
 * **AGOS writes it now, and this said otherwise until 2026-08-05.** `WebOrderDiscounts`
 * `.recordCampaignRedemption` runs `UPDATE campaigns SET redeemed_count = redeemed_count + 1`,
 * attributing to the first send of the coupon code. So the reason has moved from *structurally
 * impossible* to *nothing has run yet*: measured 2026-08-05, **15 campaigns, 1 655 sent, 0 redeemed,
 * and `campaign_sends` is empty** because no campaign has run since the migration.
 *
 * The branch below needs no change and is the reason this is a comment fix rather than a defect: it
 * tests `redeemed === 0` against the data, not against a belief about the column, so the real rate
 * appears by itself on the first redemption. A version that had hard-coded *nemerané* on the strength
 * of the sentence above would now be hiding a figure it could show.
 */
export function redemptionRate(campaigns: readonly Campaign[]): number | null {
  const sent = totalSent(campaigns)
  const redeemed = totalRedeemed(campaigns)
  if (sent === 0) return null
  // Zero redemptions across a thousand sends is the column never being written, not a rate.
  return redeemed === 0 ? null : (redeemed / sent) * 100
}

export function redemptionLabel(campaigns: readonly Campaign[]): string {
  const rate = redemptionRate(campaigns)
  return rate === null ? tt('nemerané') : formatPercent(rate, 1)
}

export function redemptionNote(campaigns: readonly Campaign[]): string {
  if (redemptionRate(campaigns) !== null) return ''
  return `${tt('Odoslaných kupónov')}: ${formatCount(totalSent(campaigns))}, ${tt('uplatnených')}: ${formatCount(totalRedeemed(campaigns))}. ${tt('Uplatnenie sa nikde nezapisuje, takže nula neznamená, že ich nikto nepoužil - znamená, že sa to nemeria.')}`
}

// ─── FB-168: a refusal recorded as a run ─────────────────────────────────────

export type RunOutcome = 'never' | 'refused' | 'reached-nobody' | 'partial' | 'delivered'

/**
 * What the last run actually was.
 *
 * Legacy prints *last run* from the row and nothing else, so a refusal and a run that reached nobody read
 * identically — and both read as *it ran*.
 *
 * **`status` decides it when the row carries one** (FB-168). `campaign_runs.status` is written from the
 * moment the column existed; every earlier row is null and genuinely does not carry the fact, so those
 * fall back to the inference below — a message with nought recipients. Inferring over a row that states
 * its own outcome would be re-deriving something already known, and the two would eventually disagree.
 */
export function runOutcome(campaign: Campaign): RunOutcome {
  const run = campaign.lastRun
  if (run === null) return 'never'
  if (run.status === 'refused') return 'refused'
  if (run.status === 'ran' && run.recipientCount === 0) return 'reached-nobody'
  if (run.status === null && run.errorMessage !== null && run.recipientCount === 0) return 'refused'
  if (run.recipientCount === 0) return 'reached-nobody'
  return run.failedCount > 0 ? 'partial' : 'delivered'
}

export function runMessage(campaign: Campaign): string {
  const run = campaign.lastRun
  if (run === null) return tt('Nikdy nebežala.')
  const outcome = runOutcome(campaign)
  if (outcome === 'refused') {
    return `${tt('Posledný pokus server odmietol')}: ${run.errorMessage ?? ''}`
  }
  if (outcome === 'reached-nobody') {
    return tt('Posledný beh nikoho nenašiel - nikto nesplnil podmienku.')
  }
  if (outcome === 'partial') {
    return `${formatCount(run.successCount)} ${tt('doručených')}, ${formatCount(run.failedCount)} ${tt('zlyhalo')}.`
  }
  return `${formatCount(run.successCount)} ${tt('doručených')}.`
}

// ─── The daily send cap ──────────────────────────────────────────────────────

/**
 * How many campaign e-mails one person may get in a day, across every campaign.
 *
 * A §10A addition with no legacy screen. `CampaignRunner`'s own comment is the reason it matters: *"No
 * per-campaign cooldown can prevent three campaigns of three different types reaching the same person on one
 * morning — each of them is within its rights. This is the only guard that spans campaigns."*
 *
 * It is off on this data — `dailySendCap: 0`, `isActive: false` — and `overDailyCap` treats **both** as off:
 * `if (!capActive) return false` and then `if (cap <= 0) return false`. So switching the flag on while the
 * number is zero changes nothing at all, silently. The form refuses that combination.
 *
 * **`PATCH /campaigns/send-cap` refuses it too now** (FB-169), against the *resulting* state rather than the
 * body, so `{isActive: true}` alone with zero already stored is caught as well. That makes this check the
 * early word rather than the only one. `capState` keeps `'on-but-zero'` regardless: the refusal stops the
 * pair being *stored* from today, and says nothing about a row that reached it before.
 *
 * `updatedAt` is null until somebody first changes the cap — it used to report the migration's own insert,
 * so an untouched cap read as *last changed 1 August*.
 */
export interface SendCap {
  dailySendCap: number
  isActive: boolean
  updatedAt: string | null
}

export function sendCapOf(payload: unknown): SendCap {
  const row = (payload ?? {}) as Record<string, unknown>
  return {
    dailySendCap: count(row.dailySendCap),
    isActive: row.isActive === true,
    updatedAt: text(row.updatedAt),
  }
}

export type CapState = 'off' | 'on-but-zero' | 'on'

export function capState(cap: SendCap): CapState {
  if (!cap.isActive) return 'off'
  // The runner's second guard: an active cap of zero is not "nobody gets mail", it is no cap at all.
  return cap.dailySendCap <= 0 ? 'on-but-zero' : 'on'
}

export function capMessage(cap: SendCap): string {
  const state = capState(cap)
  if (state === 'off') {
    return tt(
      'Limit je vypnutý - jeden zákazník môže za jedno dopoludnie dostať e-mail z každej bežiacej kampane a nič tomu nezabráni.',
    )
  }
  if (state === 'on-but-zero') {
    return tt('Limit je zapnutý, ale je nastavený na nulu - server ho v tom prípade ignoruje. Nula nie je zákaz.')
  }
  return `${tt('Jeden zákazník dostane najviac')} ${formatCount(cap.dailySendCap)} ${tt('e-mailov denne zo všetkých kampaní spolu.')}`
}

/**
 * What the cap counts, which is not what the counters above show.
 *
 * `overDailyCap` counts today's rows in `campaign_sends`, and only this system writes that log — `recordSend`
 * is its single insert. The 1 657 sends the campaign cards report came from the old system, which had no such
 * table, so the log is empty and the two numbers will not agree for a while.
 *
 * Unconditional on purpose: no endpoint reports the size of `campaign_sends`, so a page that made the note
 * conditional would be deciding on a count it invented. The sentence is true either way.
 */
export function capHistoryNote(): string {
  return tt(
    'Limit počíta správy, ktoré odoslal tento systém. Počítadlá kampaní vyššie zahŕňajú aj odoslania zo starého systému, ktorý si ich jednotlivo nezapisoval - tie sa do limitu nerátajú.',
  )
}

export function capProblems(form: { dailySendCap: string; isActive: boolean }): string[] {
  const problems: string[] = []
  const raw = form.dailySendCap.trim()
  const parsed = Number(raw.replace(',', '.'))
  if (raw === '' || !Number.isFinite(parsed)) {
    problems.push(tt('Limit musí byť číslo.'))
  } else if (!Number.isInteger(parsed)) {
    problems.push(tt('Limit je celý počet správ.'))
  } else if (parsed < 0) {
    problems.push(tt('Limit nemôže byť záporný.'))
  } else if (form.isActive && parsed === 0) {
    problems.push(tt('Zapnutý limit musí byť aspoň 1 - nulu server ignoruje.'))
  }
  return problems
}

export function capPayload(form: { dailySendCap: string; isActive: boolean }): {
  dailySendCap: number
  isActive: boolean
} {
  return {
    dailySendCap: Math.round(Number(form.dailySendCap.trim().replace(',', '.'))),
    isActive: form.isActive,
  }
}

// ─── The wheel ───────────────────────────────────────────────────────────────

export interface WheelSegment {
  type: string
  label: string
  value: number
  weight: number
  color: string
}

export interface Wheel {
  isActive: boolean
  /** The basket a customer must reach before the wheel is offered at all. `5.00` on this data. */
  minOrderCents: number
  /** How long a win stays claimable. 72 hours on this data. */
  rewardValidityHours: number
  segments: WheelSegment[]
}

export function wheelOf(payload: unknown): Wheel | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  const segments = Array.isArray(row.segments) ? row.segments : []
  const minimum = row.minOrderAmount
  return {
    isActive: row.isActive === true,
    // A decimal string, like every other money field the API sends. §14.7 keeps it in cents.
    minOrderCents: Math.round(Number(typeof minimum === 'string' ? minimum : 0) * 100),
    rewardValidityHours: count(row.rewardValidityHours),
    segments: segments.map((entry) => {
      const segment = entry as Record<string, unknown>
      return {
        type: text(segment.type) ?? '',
        label: text(segment.label) ?? '',
        value: typeof segment.value === 'number' ? segment.value : 0,
        weight: count(segment.weight),
        color: text(segment.color) ?? '',
      }
    }),
  }
}

/**
 * The chance each segment really has.
 *
 * A weight is not a percentage. The eight live segments are weighted 30/15/25/30/15/5/15/5 = **140**, so the
 * first one — labelled *10 % zľava* — is won 21,4 % of the time and not 30 %. Legacy prints the raw weight,
 * which reads as a percentage and is wrong by half again on this very wheel.
 */
export function wheelOdds(wheel: Wheel): { segment: WheelSegment; percent: number }[] {
  const total = wheel.segments.reduce((sum, segment) => sum + segment.weight, 0)
  if (total === 0) return wheel.segments.map((segment) => ({ segment, percent: 0 }))
  return wheel.segments.map((segment) => ({ segment, percent: (segment.weight / total) * 100 }))
}

export function wheelWeightTotal(wheel: Wheel): number {
  return wheel.segments.reduce((sum, segment) => sum + segment.weight, 0)
}

export function wheelWeightsLookLikePercentages(wheel: Wheel): boolean {
  return wheelWeightTotal(wheel) === 100
}

/** The chance of winning nothing, which is the number nobody puts on a wheel and everybody should. */
export function wheelLoseChance(wheel: Wheel): number | null {
  const total = wheelWeightTotal(wheel)
  if (total === 0) return null
  const losing = wheel.segments
    .filter((segment) => segment.type === 'no_win' || segment.value === 0)
    .reduce((sum, segment) => sum + segment.weight, 0)
  return (losing / total) * 100
}

export function wheelNote(wheel: Wheel): string {
  const total = wheelWeightTotal(wheel)
  const base = `${tt('Váhy spolu')}: ${formatCount(total)} - ${tt('šanca je váha delená týmto súčtom, nie samotná váha.')}`
  // §9.35 — the one case where the rule above is invisible because it happens to be a no-op. At a
  // total of 100 every weight *is* its percentage, so a wheel reads correctly right up until somebody
  // adds a segment, at which point every chance on it changes and nothing on screen said it would.
  // This warning existed, was tested, and had no caller; the note is where it belongs, because a
  // second banner for a state one wheel in the country is in would be worse than a sentence.
  if (wheelWeightsLookLikePercentages(wheel)) {
    return `${base} ${tt('Súčet je práve 100, takže váhy sa teraz čítajú ako percentá - po pridaní segmentu to prestane platiť.')}`
  }
  if (!wheel.isActive) {
    return `${tt('Koleso je vypnuté, takže sa zákazníkovi nezobrazí.')} ${base}`
  }
  return base
}

/**
 * The two conditions on the wheel that are not on the wheel.
 *
 * `minOrderAmount` and `rewardValidityHours` come back from `GET /campaigns/wheel/admin` beside the segments,
 * and decide who is offered a spin and how long a prize lasts. Both are easy to leave off a screen that draws
 * the colourful part, and both change who wins anything.
 */
export function wheelConditions(wheel: Wheel): string {
  return `${tt('Koleso sa ponúkne od objednávky za')} ${formatCents(wheel.minOrderCents)}. ${tt('Výhru si zákazník musí uplatniť do')} ${formatCount(wheel.rewardValidityHours)} ${tt('hodín, inak prepadne.')}`
}

// ─── The leaderboard and the referrals ───────────────────────────────────────

export interface LeaderboardRow {
  rank: number
  customerId: number
  customerName: string
  totalSpentCents: number
  pointsBalance: number
}

export function leaderboardOf(payload: unknown): LeaderboardRow[] {
  const rows = (payload as { leaderboard?: unknown } | undefined)?.leaderboard
  if (!Array.isArray(rows)) return []
  return rows.map((entry) => {
    const row = entry as Record<string, unknown>
    const spent = row.totalSpent
    return {
      rank: count(row.rank),
      customerId: count(row.customerId),
      customerName: text(row.customerName) ?? tt('bez menoslova'),
      // The API sends a decimal string; §14.7 keeps money in cents.
      totalSpentCents: Math.round(Number(typeof spent === 'string' ? spent : 0) * 100),
      pointsBalance: count(row.pointsBalance),
    }
  })
}

/**
 * A past month's winners, as `GET /loyalty/leaderboard/history` answers them — §9.242, FB-402.
 *
 * The endpoint has been served since the port and read by nothing. It groups `leaderboard_snapshots`
 * by month, newest first, so the shape is a list of months each carrying its own ranked winners —
 * which is the shape legacy renders and the reason this is not just another `leaderboardOf`.
 *
 * **`couponCode` is the field the live board has no equivalent for**: it is what the winner was
 * actually given, and it is the only record that a month was ever awarded.
 */
export interface LeaderboardWinner {
  id: number
  rank: number
  customerName: string
  customerEmail: string | null
  totalSpentCents: number
  couponCode: string | null
}

export interface LeaderboardMonth {
  month: string
  winners: LeaderboardWinner[]
}

export function leaderboardHistoryOf(payload: unknown): LeaderboardMonth[] {
  const months = (payload as { history?: unknown } | undefined)?.history
  if (!Array.isArray(months)) return []
  return months.flatMap((entry) => {
    const row = entry as { month?: unknown; winners?: unknown }
    const month = text(row.month)
    if (month === null) return []
    const winners = Array.isArray(row.winners) ? row.winners : []
    return [
      {
        month,
        winners: winners.map((winner) => {
          const seat = winner as Record<string, unknown>
          const spent = seat.totalSpent
          return {
            id: count(seat.id),
            rank: count(seat.rank),
            customerName: text(seat.customerName) ?? tt('bez menoslova'),
            customerEmail: text(seat.customerEmail),
            // The API sends a decimal string, as the live board does — §14.7 keeps money in cents.
            totalSpentCents: Math.round(Number(typeof spent === 'string' ? spent : 0) * 100),
            couponCode: text(seat.couponCode),
          }
        }),
      },
    ]
  })
}

/**
 * The months a leaderboard may be asked about — §9.242.
 *
 * A leaderboard is a calendar-month total, so a month that has not happened can only be empty. The
 * bound is the running month; nothing stops a past month however old, because whether it holds
 * anything is the data's answer rather than the screen's.
 */
export function latestLeaderboardMonth(now: Date): string {
  return now.toISOString().slice(0, 7)
}

export function isLeaderboardMonth(value: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(value)
}

export interface Referral {
  id: number
  status: string
  rewardPoints: number
  discountAppliedCents: number
  referrerName: string
}

export function referralsOf(payload: unknown): Referral[] {
  const rows = (payload as { referrals?: unknown } | undefined)?.referrals
  if (!Array.isArray(rows)) return []
  return rows.map((entry) => {
    const row = entry as Record<string, unknown>
    const discount = row.discountApplied
    return {
      id: count(row.id),
      status: text(row.status) ?? '',
      rewardPoints: count(row.rewardPoints),
      discountAppliedCents: Math.round(Number(typeof discount === 'string' ? discount : 0) * 100),
      referrerName: text(row.referrerName) ?? tt('bez menoslova'),
    }
  })
}

export function referralTotals(referrals: readonly Referral[]): {
  count: number
  points: number
  discountCents: number
  statuses: string[]
} {
  return {
    count: referrals.length,
    points: referrals.reduce((sum, referral) => sum + referral.rewardPoints, 0),
    discountCents: referrals.reduce((sum, referral) => sum + referral.discountAppliedCents, 0),
    statuses: [...new Set(referrals.map((referral) => referral.status))].sort(),
  }
}

/** Whether the status filter would have anything to filter. 17 of 17 are `rewarded` on this data. */
export function referralStatusesWorthFiltering(referrals: readonly Referral[]): boolean {
  return referralTotals(referrals).statuses.length > 1
}

// ─── §9.5's multi-campaign: making a second one of a type ────────────────────

/**
 * Creating, scheduling and removing campaigns — the last of §9.5's four backend upgrades.
 *
 * §10A.2 let a type hold several campaigns and gave them six endpoints under `/campaigns/id/{id}`, kept
 * deliberately separate from the by-type ones so *"nothing already written against /{type} has to change
 * to keep working"*. Measured: **15 campaigns of 15 types**, so no type has a second one and the whole
 * by-id half has never been reachable.
 *
 * **Three rules that are the server's, not this screen's.**
 *
 * 1. **A new campaign is created switched off.** `INSERT … is_active` is the literal `false`, whatever
 *    was sent. Right, and worth saying: a campaign that started sending the moment somebody typed a name
 *    would be a mail-out nobody reviewed.
 * 2. **The first campaign of a type cannot be deleted** — *"Základnú kampaň tohto typu nemožno zmazať —
 *    vypni ju namiesto toho"*. The list is meant to show every type, switched off if unused, and the
 *    `/{type}` endpoints have to keep resolving to something.
 * 3. **The window is validated, not stored blindly.** `boundary` refuses anything `Instants.parseOrNull`
 *    cannot read, per field. Measured: **0 of 15** campaigns carry a `starts_at` or an `ends_at`, so
 *    scheduling has never been used either.
 *
 * And the settings are not blank: `create` starts from `CampaignCatalog.defaultSettings(type)` and merges
 * what was sent over the top. So a campaign created here is a working one of that type, which is why this
 * form does not ask for settings at all — it asks for the three things that are genuinely per-campaign,
 * and the rest is edited where it already is.
 */

export interface NewCampaign {
  type: string
  name: string
  startsAt: string
  endsAt: string
}

export const EMPTY_NEW_CAMPAIGN: NewCampaign = { type: '', name: '', startsAt: '', endsAt: '' }

/** A `datetime-local` value, or a date, or nothing. Anything else the server would refuse by name. */
function isStamp(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?)?$/.test(value.trim())
}

export function newCampaignProblems(form: NewCampaign): string[] {
  const found: string[] = []
  if (!CAMPAIGN_TYPES.includes(form.type)) found.push(tt('Vyberte typ kampane'))
  if (form.name.trim() === '') {
    // Not the server's rule — its column is nullable — but a second campaign of a type that shares its
    // neighbour's name is indistinguishable in every list, including this one. FB-166 named that problem.
    found.push(tt('Pomenujte kampaň - bez názvu ju od tej vedľa nerozoznáte'))
  }
  if (form.startsAt !== '' && !isStamp(form.startsAt)) found.push(tt('Neplatný dátum: startsAt'))
  if (form.endsAt !== '' && !isStamp(form.endsAt)) found.push(tt('Neplatný dátum: endsAt'))
  if (form.startsAt !== '' && form.endsAt !== '' && isStamp(form.startsAt) && isStamp(form.endsAt)) {
    if (form.endsAt.trim() <= form.startsAt.trim()) {
      found.push(tt('Koniec musí byť po začiatku - inak kampaň nikdy nebeží'))
    }
  }
  return found
}

/** The body `POST /campaigns` takes. An empty window is `null`, not an empty string. */
export function newCampaignPayload(form: NewCampaign): Record<string, unknown> {
  return {
    type: form.type,
    name: form.name.trim(),
    // §9.262 — through `offsetStampOf` like the window editor's. Two builders writing the same two
    // columns with different meanings is how a campaign created here and one edited there disagree
    // about when they start.
    startsAt: form.startsAt.trim() === '' ? null : offsetStampOf(form.startsAt.trim()),
    endsAt: form.endsAt.trim() === '' ? null : offsetStampOf(form.endsAt.trim()),
  }
}

/** Said under the button, because a campaign that ran on creation would be a mail-out nobody reviewed. */
export function creationNote(): string {
  return tt(
    'Nová kampaň sa založí vypnutá - server ju vytvorí neaktívnu, nech si ju najprv prezriete. Nastavenia dostane predvolené pre svoj typ.',
  )
}

/**
 * Whether this campaign can be deleted at all.
 *
 * The lowest id of a type is its primary and the server refuses to remove it. That is not visible on the
 * row, so it is computed from the list the screen already has, and the button explains itself rather than
 * failing on click.
 */
export function mayDelete(campaign: Campaign, campaigns: readonly Campaign[]): boolean {
  const ofType = campaigns.filter((other) => other.type === campaign.type)
  if (ofType.length <= 1) return false
  const primary = ofType.reduce((lowest, other) => (other.id < lowest.id ? other : lowest))
  return primary.id !== campaign.id
}

export function deleteRefusalNote(): string {
  return tt(
    'Toto je základná kampaň svojho typu - zmazať sa nedá, lebo zoznam má ukázať každý typ a ostatné rozhrania sa naň odvolávajú. Vypnite ju namiesto toho.',
  )
}

// `perCampaignSends` was here and is deleted — §9.35, 2026-08-06. It gated the card's send figure on
// `campaign_sends` having rows, answering *nemerané* while that table is empty. The premise was
// wrong: the card shows `campaigns.sent_count`, a counter `CampaignRunner` and `PersonalisedOffer`
// increment directly, and it holds real history — 1 655 sends across six campaigns on this data,
// 616 for *reaktivácia neaktívnych* alone. Wiring the function would have hidden all of it behind
// "unmeasured". It was never called, so nothing was ever hidden; it is removed rather than left as a
// trap for whoever wires the list next.

/**
 * One editable field of a campaign's settings — §9.141, the owner's points 32 and 33.
 *
 * <p><b>Typed from the value, because that is the only description these fields have.</b> The
 * backend's catalogue holds defaults per type — `{cashbackPercent: 5, minOrderValue: 20}`,
 * `{startTime: '14:00', bonusPointsPercent: 30}` — and no schema beyond them. Inventing a richer one
 * would be asserting a contract the server does not offer.
 */
export type CampaignFieldKind = 'number' | 'time' | 'boolean' | 'text'

export interface CampaignField {
  key: string
  kind: CampaignFieldKind
  value: string | number | boolean
}

const TIME_KEY = /(^|[a-z])time$/i
const HH_MM = /^([01]?\d|2[0-3]):[0-5]\d$/

/**
 * What each campaign setting is called, in Slovak — §9.273, owner O13.
 *
 * *"in campaigns, would be good if you also replace placeholders.. like for example minOrderValue,
 * cashbackPercent etc."* The drawer rendered `{field.key}` as the label, so the editor read
 * `minOrderValue`, `cashbackPercent`, `couponValidityHours` — the raw camelCase keys of the stored
 * `settings` jsonb. Every one of the twenty-five keys the fifteen campaigns actually carry is named
 * here, read off the live rows rather than off the catalogue, so nothing that exists is missed.
 *
 * **An unknown key keeps its own name**, marked, rather than being guessed at: a settings key this
 * build has never seen is a new backend field, and silently inventing a Slovak name for it is how a
 * screen comes to describe something it does not understand.
 */
/* eslint-disable no-restricted-syntax -- These are the *sources* a translation is looked up by, not
   text rendered from here: `campaignFieldLabel` passes each through `tt()` at the point of display.
   Wrapping them at definition would freeze the lookup at module load, so a language change would
   leave the labels in Slovak. FRONTEND.md §8/§14.2 asks for the call, and the call is below. */
const FIELD_LABELS: Record<string, string> = {
  minOrderValue: 'Minimálna hodnota objednávky (€)',
  discountPercent: 'Zľava (%)',
  discountAmount: 'Zľava (€)',
  discountValue: 'Hodnota zľavy',
  discountType: 'Typ zľavy (percent / fixed)',
  cashbackPercent: 'Cashback (%)',
  bonusPointsPercent: 'Bonusové body navyše (%)',
  validityDays: 'Platnosť kupónu (dni)',
  expiryDays: 'Platnosť kupónu (dni)',
  couponValidityHours: 'Platnosť kupónu (hodiny)',
  couponPrefix: 'Predpona kódu kupónu',
  frequencyPerDay: 'Počet hier za deň',
  // Read by **nothing** on the server — `frequencyPerDay` beside it is the one `MiniGameService`
  // uses. §9.276 moved the warning out of the label and into `inertNote`, so all three dead settings
  // say it the same way.
  playsPerDay: 'Počet hier za deň',
  orderCount: 'Počet objednávok potrebných na kupón',
  inactiveMonths: 'Neaktívny aspoň (mesiace)',
  topN: 'Koľkí najlepší dostanú odmenu',
  targetSegment: 'Komu sa pošle (segment)',
  promoLabel: 'Názov akcie na účte',
  stackableWithCoupons: 'Dá sa kombinovať s kupónom',
  daysOfWeek: 'Dni v týždni (1 = pondelok)',
  startTime: 'Začiatok',
  endTime: 'Koniec',
  pushTitle: 'Nadpis notifikácie',
  pushBody: 'Text notifikácie',
  pushUrl: 'Odkaz notifikácie',
}

/* eslint-enable no-restricted-syntax */

export function campaignFieldLabel(key: string): string {
  const known = FIELD_LABELS[key]
  return known === undefined ? key : tt(known)
}

/** Whether this build knows what the key means — an unknown one is drawn as the developer name. */
export function isKnownField(key: string): boolean {
  return FIELD_LABELS[key] !== undefined
}

/**
 * Settings the server stores and never reads — §9.276, FB-434.
 *
 * **Keyed by `(type, key)`, because inertness is.** `minOrderValue` is read for `meninova_zlava`,
 * `kuponove_tornado`, `reaktivacia_neaktivnych` and `kupon_za_vernost`, and ignored by the two games,
 * whose coupon is written with `min_order_amount` hardcoded to `0` — **as legacy's is**
 * (`mini-game.ts:220`), so it is faithful rather than broken. A note keyed on the setting alone would
 * warn about four campaigns where it works.
 *
 * `''` for everything else, so the caller does not have to know which case it is in.
 */
export function inertNote(type: string, key: string): string {
  if (key === 'playsPerDay') {
    return tt('Server toto pole nečíta - počet hier riadi „Počet hier za deň“ nad ním.')
  }
  if (key === 'minOrderValue' && (type === 'click_and_win' || type === 'scratch_card')) {
    return tt('Server toto pole pri hrách nečíta - vyhraný kupón platí bez minimálnej sumy.')
  }
  if (key === 'cashbackPercent') {
    return tt('Cashback zatiaľ nie je naprogramovaný - hodnota sa uloží a nič nespraví.')
  }
  return ''
}

export function campaignFieldKind(key: string, value: unknown): CampaignFieldKind {
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') return 'number'
  // A time is a string that both looks like one and is named like one — `startTime: '14:00'`.
  // Either test alone is wrong: `name: '10:00'` is not a time, and `startTime: 'ráno'` is not one
  // a time input can hold.
  if (typeof value === 'string' && TIME_KEY.test(key) && HH_MM.test(value)) return 'time'
  return 'text'
}

/**
 * The fields a campaign offers, in the order its settings are stored.
 *
 * <p>Stored order rather than alphabetical: the backend writes them in the order the catalogue
 * declares, which groups related knobs together — `startTime` beside `endTime` — and sorting would
 * scatter them.
 */
/**
 * Settings this editor cannot draw, named rather than dropped - §9.310, owner O9, FB-486.
 *
 * `campaignFields` keeps scalars and silently discards anything structured, which is right for the
 * inputs it renders and wrong as the whole story: `slots` is the mini-game **prize table** that
 * `MiniGameService.draw` picks a winner from, and `segments` is who a personalised offer targets.
 * Both are stored, both are honoured, and neither appeared anywhere on the screen.
 *
 * **A screen that shows four of a campaign's six settings is worse than one that shows four and says
 * there are six.** That is the rule `isKnownField` already applies to a key whose meaning this build
 * does not know; this applies it to a value whose shape it cannot edit.
 */
export function unshownSettings(settings: unknown): string[] {
  if (typeof settings !== 'object' || settings === null || Array.isArray(settings)) return []
  return Object.entries(settings as Record<string, unknown>)
    .filter(([, value]) => value !== null && typeof value === 'object')
    .map(([key]) => key)
}

/** What to tell the operator about them. Empty when there are none, which is most campaigns. */
export function unshownSettingsNote(settings: unknown): string {
  const keys = unshownSettings(settings)
  if (keys.length === 0) return ''
  return `${tt('Táto kampaň má aj nastavenia, ktoré sa tu zatiaľ nedajú upraviť')}: ${keys.join(', ')}`
}

/**
 * The channels a campaign may send on - §9.310.
 *
 * **The server has read this since the port and nothing could write it.** `CampaignRunner.channelOn`
 * looks for `settings.channels` and, finding none, answers *e-mail on, push on, SMS off* for every
 * campaign. None of the fifteen carries the key, so that fallback has always been the whole
 * behaviour and the owner has never been able to change it.
 *
 * The defaults here are the server's own fallback, so drawing the switches on a campaign that has no
 * `channels` shows what it is actually doing rather than a guess.
 */
export const CAMPAIGN_CHANNELS: readonly string[] = ['email', 'push', 'sms']

export interface CampaignChannels {
  email: boolean
  push: boolean
  sms: boolean
}

export function channelsOf(settings: unknown): CampaignChannels {
  const stored =
    typeof settings === 'object' && settings !== null && !Array.isArray(settings)
      ? (settings as Record<string, unknown>).channels
      : undefined
  const map =
    typeof stored === 'object' && stored !== null && !Array.isArray(stored)
      ? (stored as Record<string, unknown>)
      : {}
  // `channelOn`'s rule, restated so the screen and the runner cannot drift: e-mail and push are on
  // unless explicitly false, SMS is off unless explicitly true.
  return {
    email: map.email !== false,
    push: map.push !== false,
    sms: map.sms === true,
  }
}

export function campaignFieldLabelForChannel(channel: string): string {
  if (channel === 'email') return tt('E-mail')
  if (channel === 'push') return tt('Push notifikácia')
  return tt('SMS')
}

export function campaignFields(settings: unknown): CampaignField[] {
  if (typeof settings !== 'object' || settings === null || Array.isArray(settings)) return []
  return Object.entries(settings as Record<string, unknown>).flatMap((entry) => {
    const [key, value] = entry
    if (value === null || typeof value === 'object') return []
    return [{ key, kind: campaignFieldKind(key, value), value: value as string | number | boolean }]
  })
}

/**
 * What to PATCH: only the keys somebody actually changed.
 *
 * <p><b>The server merges settings rather than replacing them</b>, deliberately — a campaign whose
 * wording was customised must not lose it because a discount was toggled. Sending the whole object
 * back would overwrite keys this screen never showed, which is the exact loss the merge prevents.
 */
export function changedSettings(
  original: unknown,
  edited: Readonly<Record<string, string | number | boolean>>,
  /**
   * The channels, when the operator touched them - §9.310.
   *
   * **Passed separately because it is the one setting this editor can *create*.** Everything else
   * here is an edit to a key the campaign already had; `channels` is stored by none of the fifteen,
   * so a loop over what exists could never produce it. Omitted when unchanged, so a save that did not
   * touch the switches does not start writing a key that was not there.
   */
  channels?: CampaignChannels,
): Record<string, unknown> {
  const before = campaignFields(original)
  const changed: Record<string, unknown> = {}
  for (const field of before) {
    const next = edited[field.key]
    if (next === undefined) continue
    if (next !== field.value) changed[field.key] = next
  }
  if (channels !== undefined) {
    const stored = channelsOf(original)
    const moved =
      channels.email !== stored.email || channels.push !== stored.push || channels.sms !== stored.sms
    // Written whole rather than key by key: the server merges the *settings* object, not the map
    // inside it, so a partial `channels` would drop the two the operator did not touch.
    if (moved) changed.channels = { ...channels }
  }
  return changed
}

/** A number field parsed back from its input, refusing what a settings value cannot be. */
export function parseFieldValue(kind: CampaignFieldKind, raw: string): string | number | boolean | null {
  if (kind === 'number') {
    // `Number('')` is 0, not NaN — so a cleared field would save a silent zero, and a cashback of
    // 0 % is a campaign that gives nothing while looking configured. Blank is refused.
    if (raw.trim() === '') return null
    const parsed = Number(raw)
    // Not `NaN`, and not negative: every numeric setting here is a percentage, a count or a money
    // floor, and none of them is meaningful below zero.
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
  }
  if (kind === 'time') return HH_MM.test(raw) ? raw : null
  if (kind === 'boolean') return raw === 'true'
  return raw
}

// ─── §9.160 — editing the wheel ──────────────────────────────────────────────
//
// The card could always show the wheel and never change it: no switch, no editor, `is_active` false
// in the data and no way to make it true. The server has always taken all four fields.

export interface WheelForm {
  minOrder: string
  rewardValidityHours: string
  segments: Array<{ label: string; weight: string; color: string; type: string; value: string }>
}

/** The stored wheel as a form fills it — money and counts as the text somebody types. */
export function wheelFormOf(wheel: Wheel): WheelForm {
  return {
    // A comma, because this lands in an input a Slovak owner reads and types into.
    // `wheelPayload` turns it back before it goes on the wire.
    minOrder: formatDecimal(wheel.minOrderCents / 100, 2),
    rewardValidityHours: String(wheel.rewardValidityHours),
    segments: wheel.segments.map((segment) => ({
      label: segment.label,
      weight: String(segment.weight),
      color: segment.color,
      type: segment.type,
      value: String(segment.value),
    })),
  }
}

export type WheelProblem =
  | { kind: 'no-segments' }
  | { kind: 'no-weight' }
  | { kind: 'bad-number'; field: string }
  | { kind: 'empty-label' }

/**
 * What stops the wheel being saved.
 *
 * <p><b>Every weight at zero is the one that matters.</b> A spin picks a segment in proportion to its
 * weight; with nothing to divide by there is no answer, and a wheel that cannot choose is worse than
 * one that is switched off — it is switched on and broken. The server validates its own copy, but a
 * customer spinning is not the moment to find out.
 */
export function wheelProblems(form: WheelForm): WheelProblem[] {
  const problems: WheelProblem[] = []
  if (form.segments.length === 0) problems.push({ kind: 'no-segments' })
  if (form.segments.some((segment) => segment.label.trim() === '')) {
    problems.push({ kind: 'empty-label' })
  }
  for (const [field, raw] of [
    ['minOrder', form.minOrder],
    ['rewardValidityHours', form.rewardValidityHours],
  ] as const) {
    const value = Number(raw.replace(',', '.'))
    if (!Number.isFinite(value) || value < 0) problems.push({ kind: 'bad-number', field })
  }
  const weights = form.segments.map((segment) => Number(segment.weight))
  if (weights.some((weight) => !Number.isFinite(weight) || weight < 0)) {
    problems.push({ kind: 'bad-number', field: 'weight' })
  } else if (form.segments.length > 0 && weights.reduce((sum, weight) => sum + weight, 0) === 0) {
    problems.push({ kind: 'no-weight' })
  }
  return problems
}

export function wheelProblemMessage(problem: WheelProblem): string {
  if (problem.kind === 'no-segments') return tt('Koleso potrebuje aspoň jedno políčko.')
  if (problem.kind === 'empty-label') return tt('Každé políčko potrebuje názov - zákazník ho uvidí.')
  if (problem.kind === 'no-weight') {
    return tt('Aspoň jedno políčko musí mať váhu väčšiu ako 0, inak sa koleso nemá ako rozhodnúť.')
  }
  return tt('Zadajte platné číslo.')
}

/** What the form sends. Money as the decimal string the API takes, counts as numbers. */
export function wheelPayload(form: WheelForm): Record<string, unknown> {
  return {
    // A wire format, not a screen: the API takes a decimal string with a dot, and `formatDecimal`
    // would send it a comma.
    // eslint-disable-next-line no-restricted-syntax
    minOrderAmount: Number(form.minOrder.replace(',', '.')).toFixed(2),
    rewardValidityHours: Math.round(Number(form.rewardValidityHours)),
    segments: form.segments.map((segment) => ({
      type: segment.type,
      label: segment.label.trim(),
      value: Number(segment.value.replace(',', '.')),
      weight: Number(segment.weight),
      color: segment.color,
    })),
  }
}

/** The odds a set of weights produces, so the editor can show them while they are typed. */
export function weightOdds(form: WheelForm): number[] {
  const weights = form.segments.map((segment) => Math.max(0, Number(segment.weight) || 0))
  const total = weights.reduce((sum, weight) => sum + weight, 0)
  return total === 0 ? weights.map(() => 0) : weights.map((weight) => (weight / total) * 100)
}

// ─── §9.339 - the guard in front of the SMS switch ───────────────────────────
//
// `BACKEND.md` §10A.116 moved the guard from the send to the switch, and this is why: *"that is the
// irreversible decision - after it, every trigger costs money quietly."* The owner's terms for the
// whole feature were *"a recipient count and a cost shown before the button, and a typed
// confirmation, the way the manual SMS sender already works."*
//
// Turning it **off** is not guarded, deliberately: a confirmation on the corrective action is a toll
// on fixing a mistake. The same asymmetry `SAAS.md` §3.3's *Všetky / Žiadne* has.

/** 2,6 cents a segment - the price this product already quotes, shared with the manual sender. */
export const SMS_SEGMENT_CENTS = 2.6

/**
 * Why the SMS channel cannot be switched on at all, or the empty string when it can.
 *
 * **A blank template is the first thing anybody will hit.** `campaign_sms_template` is `''` on the
 * owner's live data with `campaign_sms_enabled` true, and §10A.128 refuses the *run* for the same
 * reason - so the switch refuses first, rather than letting somebody type a confirmation phrase for
 * something that will not send.
 */
export function smsSwitchBlocked(template: string | null | undefined): string {
  if ((template ?? '').trim() === '') {
    return tt(
      'Text kampaňovej SMS je prázdny. Napíšte ho v Nastavenia -> SMS, inak by zákazníkom odišla prázdna správa.',
    )
  }
  return ''
}

/**
 * What turning it on will cost, per trigger.
 *
 * **Per trigger and not per blast**, because that is what a campaign is: §10A.116 chose the shape
 * over a blast button precisely so one click cannot text everybody. The figure quoted is therefore
 * the campaign's own reach - the worst case for *this* campaign, which for
 * `reaktivacia_neaktivnych` can be hundreds in a day.
 */
export function smsRunCostCents(recipients: number): number {
  return Math.round(Math.max(0, recipients) * SMS_SEGMENT_CENTS)
}

/**
 * The sentence in front of the switch.
 *
 * Both numbers, because either alone is misleading: a count with no price does not say it costs
 * anything, and a price with no count cannot be checked against what the campaign matches.
 */
export function smsSwitchWarning(recipients: number): string {
  return `${tt('Zapnutím SMS začne táto kampaň pri každom spustení posielať SMS až')} ${formatCount(recipients)} ${tt('zákazníkom - približne')} ${formatCents(smsRunCostCents(recipients))} ${tt('za jedno spustenie.')}`
}

/**
 * The phrase somebody has to type, and it names the channel rather than being the word *yes*.
 *
 * The `anonymisePhrase` / `anonymiseConfirmed` pair on `/marketing-customers` is the precedent, and
 * it is re-used rather than re-invented: one confirmation idiom on this product, compared the same
 * way. Case- and space-insensitively, because legacy compared with `trim()` alone and refused a
 * lowercase answer without saying why.
 */
export function smsConfirmPhrase(): string {
  return 'ZAPNUT SMS'
}

export function smsConfirmed(typed: string): boolean {
  return typed.trim().replace(/\s+/gu, ' ').toUpperCase() === smsConfirmPhrase()
}
